import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import OneDriveFilePreview from './OneDriveFilePreview'
import toast from 'react-hot-toast'
import {
  Folder,
  FileText,
  ChevronRight,
  Loader2,
  Cloud,
  AlertCircle,
  Upload,
  Plus,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  Download,
  Eye,
} from 'lucide-react'

export interface OneDriveItem {
  path: string
  name: string
  isDirectory: boolean
  size: number | null
  mimeType: string | null
}

interface BrowseResult {
  path: string
  items: OneDriveItem[]
}

interface OneDriveFileBrowserProps {
  className?: string
}

export default function OneDriveFileBrowser({ className = '' }: OneDriveFileBrowserProps) {
  const [currentPath, setCurrentPath] = useState('')
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null)
  const [renameState, setRenameState] = useState<{ path: string; name: string; isDirectory: boolean } | null>(null)
  const [preview, setPreview] = useState<{ path: string; name: string; mimeType: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const invalidate = () => queryClient.invalidateQueries(['onedrive-browse'])

  useEffect(() => {
    if (!openMenuKey) return
    const close = (e: MouseEvent) => {
      if ((e.target as Element)?.closest?.('[data-od-menu]')) return
      setOpenMenuKey(null)
    }
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [openMenuKey])

  const { data, isLoading, error, isFetching } = useQuery<BrowseResult>(
    ['onedrive-browse', currentPath],
    async () => {
      const res = await api.get('/onedrive/browse', { params: { path: currentPath || undefined } })
      return res.data.data as BrowseResult
    },
    { retry: 1, refetchOnWindowFocus: false }
  )

  const createFolder = useMutation(
    async (name: string) => {
      await api.post('/onedrive/folders', { name, path: currentPath })
    },
    {
      onSuccess: () => {
        invalidate()
        setShowNewFolder(false)
        setNewFolderName('')
        toast.success('Ordner erstellt')
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } }
        toast.error(err.response?.data?.error || 'Ordner konnte nicht erstellt werden')
      },
    }
  )

  const uploadFiles = useMutation(
    async (files: File[]) => {
      const form = new FormData()
      files.forEach((f) => form.append('files', f))
      form.append('path', currentPath)
      await api.post('/onedrive/upload', form)
    },
    {
      onSuccess: () => {
        invalidate()
        toast.success('Dateien hochgeladen')
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } }
        toast.error(err.response?.data?.error || 'Upload fehlgeschlagen')
      },
    }
  )

  const deleteItem = useMutation(
    async (path: string) => {
      await api.delete('/onedrive/files', { params: { path } })
    },
    {
      onSuccess: () => { setOpenMenuKey(null); invalidate(); toast.success('Gelöscht') },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } }
        toast.error(err.response?.data?.error || 'Löschen fehlgeschlagen')
      },
    }
  )

  const renameItem = useMutation(
    async ({ path, name }: { path: string; name: string }) => {
      await api.patch('/onedrive/files', { path, name })
    },
    {
      onSuccess: () => { setRenameState(null); setOpenMenuKey(null); invalidate(); toast.success('Umbenannt') },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } }
        toast.error(err.response?.data?.error || 'Umbenennen fehlgeschlagen')
      },
    }
  )

  const copyItem = useMutation(
    async (path: string) => {
      await api.post('/onedrive/files/copy', { path })
    },
    {
      onSuccess: () => { setOpenMenuKey(null); invalidate(); toast.success('Kopie erstellt') },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } }
        toast.error(err.response?.data?.error || 'Kopieren fehlgeschlagen')
      },
    }
  )

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files
    if (!list?.length) return
    uploadFiles.mutate(Array.from(list))
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const list = e.dataTransfer.files
    if (!list?.length) return
    uploadFiles.mutate(Array.from(list))
  }

  const pathSegments = currentPath ? currentPath.split('/').filter(Boolean) : []
  const breadcrumbs = [
    { label: 'OneDrive', path: '' },
    ...pathSegments.map((seg, idx) => ({
      label: seg,
      path: pathSegments.slice(0, idx + 1).join('/'),
    })),
  ]

  const base = api.defaults.baseURL || '/api'

  if (error) {
    const errMsg = (error as { response?: { data?: { error?: string } } })?.response?.data?.error
      || 'OneDrive-Dateien konnten nicht geladen werden'
    return (
      <div className={`rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">{errMsg}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap text-sm min-w-0">
          <Cloud className="h-4 w-4 text-blue-600 flex-shrink-0" />
          {breadcrumbs.map((crumb, idx) => (
            <span key={crumb.path || 'root'} className="flex items-center gap-1">
              {idx > 0 && <ChevronRight className="h-3 w-3 text-gray-400" />}
              <button
                type="button"
                onClick={() => setCurrentPath(crumb.path)}
                className={`hover:text-primary-600 ${idx === breadcrumbs.length - 1 ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-500'}`}
              >
                {crumb.label}
              </button>
            </span>
          ))}
          {(isLoading || isFetching) && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadFiles.isLoading}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
          >
            {uploadFiles.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Hochladen
          </button>
          <button
            type="button"
            onClick={() => setShowNewFolder(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            Neuer Ordner
          </button>
        </div>
      </div>

      {showNewFolder && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/80">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Ordnername"
            className="input input-sm flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newFolderName.trim()) createFolder.mutate(newFolderName.trim())
              if (e.key === 'Escape') setShowNewFolder(false)
            }}
          />
          <button type="button" className="btn btn-sm btn-primary" disabled={!newFolderName.trim() || createFolder.isLoading} onClick={() => createFolder.mutate(newFolderName.trim())}>
            Erstellen
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowNewFolder(false)}>Abbrechen</button>
        </div>
      )}

      <div
        className={`relative rounded-lg border-2 border-dashed ${isDragging ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30' : 'border-transparent'}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-primary-100/90 text-sm font-medium text-primary-700">
            Dateien hier ablegen …
          </div>
        )}
        <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-900">
          {isLoading ? (
            <li className="flex items-center gap-2 px-4 py-8 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Lade OneDrive-Dateien…
            </li>
          ) : (data?.items?.length ?? 0) === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-gray-500">Dieser Ordner ist leer.</li>
          ) : (
            data?.items?.map((item) => {
              const menuKey = item.path
              const Icon = item.isDirectory ? Folder : FileText
              return (
                <li key={item.path} className="flex items-center gap-1 group">
                  {item.isDirectory ? (
                    <button
                      type="button"
                      onClick={() => setCurrentPath(item.path)}
                      className="flex flex-1 min-w-0 items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/80"
                    >
                      <Icon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      {renameState?.path === item.path ? (
                        <input
                          className="flex-1 rounded border px-2 py-1 text-sm dark:bg-gray-800"
                          defaultValue={renameState.name}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') renameItem.mutate({ path: item.path, name: (e.target as HTMLInputElement).value })
                            if (e.key === 'Escape') setRenameState(null)
                          }}
                        />
                      ) : (
                        <span className="font-medium text-gray-900 truncate dark:text-gray-100">{item.name}</span>
                      )}
                      <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPreview({ path: item.path, name: item.name, mimeType: item.mimeType || 'application/octet-stream' })}
                      className="flex flex-1 min-w-0 items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/80"
                    >
                      <Icon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      {renameState?.path === item.path ? (
                        <input
                          className="flex-1 rounded border px-2 py-1 text-sm dark:bg-gray-800"
                          defaultValue={renameState.name}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') renameItem.mutate({ path: item.path, name: (e.target as HTMLInputElement).value })
                            if (e.key === 'Escape') setRenameState(null)
                          }}
                        />
                      ) : (
                        <>
                          <span className="text-sm text-gray-900 truncate dark:text-gray-100">{item.name}</span>
                          {item.size != null && item.size > 0 && (
                            <span className="text-xs text-gray-500 ml-auto">{formatBytes(item.size)}</span>
                          )}
                        </>
                      )}
                    </button>
                  )}
                  <div className="relative pr-2" data-od-menu>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setOpenMenuKey(openMenuKey === menuKey ? null : menuKey) }}
                      className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {openMenuKey === menuKey && (
                      <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-md border bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900" data-od-menu>
                        {!item.isDirectory && (
                          <>
                            <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setPreview({ path: item.path, name: item.name, mimeType: item.mimeType || 'application/octet-stream' }); setOpenMenuKey(null) }}>
                              <Eye className="h-4 w-4" /> Ansehen / Bearbeiten
                            </button>
                            <a href={`${base}/onedrive/files/download?path=${encodeURIComponent(item.path)}`} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => setOpenMenuKey(null)}>
                              <Download className="h-4 w-4" /> Herunterladen
                            </a>
                          </>
                        )}
                        <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setRenameState({ path: item.path, name: item.name, isDirectory: item.isDirectory }); setOpenMenuKey(null) }}>
                          <Pencil className="h-4 w-4" /> Umbenennen
                        </button>
                        {!item.isDirectory && (
                          <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => copyItem.mutate(item.path)}>
                            <Copy className="h-4 w-4" /> Kopieren
                          </button>
                        )}
                        <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40" onClick={() => { if (confirm(`„${item.name}" wirklich löschen?`)) deleteItem.mutate(item.path) }}>
                          <Trash2 className="h-4 w-4" /> Löschen
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              )
            })
          )}
        </ul>
      </div>

      {preview && (
        <OneDriveFilePreview
          isOpen={!!preview}
          onClose={() => setPreview(null)}
          filePath={preview.path}
          fileName={preview.name}
          mimeType={preview.mimeType}
        />
      )}
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
